import * as React from 'react';
import propz from 'propz';
import { History } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import { getStudentProgrammeBookingInbox } from 'Src/helpers/service/student/clubProgrammesBooking';
import { ProgrammeBookingInboxItem } from 'Src/views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.types';
import { programmeBookingFormatCountdown } from 'Src/helpers/clubProgramme/clubProgrammeBooking';
import { StudentPageHeader } from '../components/StudentPageHeader';
import '../components/StudentHub.scss';
import './StudentClubProgrammes.scss';
import { ClubSectionCounts, clubSectionTabs, getStudentClubSectionCounts } from 'Src/helpers/club/clubSectionCounts';

interface Props {
  user: AppUser;
  history: History;
}

export const StudentClubProgrammes: React.FC<Props> = ({ user, history }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [rows, setRows] = React.useState<ProgrammeBookingInboxItem[]>([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());
  const [sectionCounts, setSectionCounts] = React.useState<ClubSectionCounts | undefined>();

  React.useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const studentPermission = React.useMemo(() => {
    const activePermission = propz.get(user, ['activePermission'], null);
    return activePermission || null;
  }, [user]);

  const schoolId = studentPermission ? String(propz.get(studentPermission, ['schoolId'], '')) : '';
  const playerPermissionId = studentPermission
    ? String(propz.get(studentPermission, ['id'], propz.get(studentPermission, ['_id'], '')))
    : '';

  const load = React.useCallback(async () => {
    try {
      setError(undefined);
      setIsLoading(true);

      if (!schoolId) {
        setRows([]);
        setError('Student school is missing');
        return;
      }

      const result = await getStudentProgrammeBookingInbox(user, schoolId, playerPermissionId);
      const nextRows = Array.isArray(result) ? result : [];
      setRows(nextRows);
      getStudentClubSectionCounts(user, { browse: nextRows.length })
        .then(setSectionCounts)
        .catch(() => setSectionCounts(undefined));
    } catch (e) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as any).message || '') : '';

      setError(msg || 'Failed to load programmes');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, schoolId, playerPermissionId]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (isLoading) return <Loader />;

  return (
    <main className="parentPage studentHubPage studentProgrammesPage">
      <StudentPageHeader
        title="Clubs"
        tabs={clubSectionTabs(sectionCounts, 'browse', history)}
        chips={[
          {
            value: rows.length,
            label: rows.length === 1 ? 'programme available' : 'programmes available',
            tone: rows.length ? 'green' : 'slate'
          }
        ]}
        actions={
          <button className="parentButton" type="button" onClick={load}>
            Refresh
          </button>
        }
      />

      {error ? (
        <div className="studentHubAlert" role="alert">
          {error}
        </div>
      ) : null}

      {!rows.length ? (
        <div className="studentHubEmpty">
          <span className="studentHubEmpty__icon">C</span>
          <strong>No clubs available</strong>
          <p>New programmes will appear here when your school opens them for booking.</p>
        </div>
      ) : (
        <div className="studentProgrammeGrid">
          {rows.map(row => {
            const id = String(row.id || '');
            const opensAt = propz.get(row, ['booking', 'opensAt'], null);
            const closesAt = propz.get(row, ['booking', 'closesAt'], null);
            const access = String(propz.get(row, ['booking', 'preBookingAccess'], '') || '').toUpperCase();

            const pending = Number(propz.get(row, ['counts', 'pending'], 0)) || 0;
            const draft = Number(propz.get(row, ['counts', 'draft'], 0)) || 0;
            const accepted = Number(propz.get(row, ['counts', 'accepted'], 0)) || 0;

            const windowText = [
              opensAt ? `Opens: ${new Date(opensAt).toLocaleString()}` : undefined,
              closesAt ? `Closes: ${new Date(closesAt).toLocaleString()}` : undefined
            ]
              .filter(Boolean)
              .join(' • ');

            const opensAtMs = opensAt ? new Date(opensAt).getTime() : null;
            const closesAtMs = closesAt ? new Date(closesAt).getTime() : null;

            const isBeforeOpen = !!(opensAtMs && nowMs < opensAtMs);
            const isOpen = !!(opensAtMs && closesAtMs && nowMs >= opensAtMs && nowMs < closesAtMs);
            const isClosed = !!(closesAtMs && nowMs >= closesAtMs);

            const secToOpen = opensAtMs && nowMs < opensAtMs ? Math.ceil((opensAtMs - nowMs) / 1000) : null;
            const secToClose = closesAtMs && nowMs < closesAtMs ? Math.ceil((closesAtMs - nowMs) / 1000) : null;

            const countdownText =
              secToOpen !== null
                ? `Opens in ${programmeBookingFormatCountdown(secToOpen)}`
                : secToClose !== null
                ? `Closes in ${programmeBookingFormatCountdown(secToClose)}`
                : isClosed
                ? 'Booking closed'
                : '';

            const showSummaryChips = isOpen || access === 'DRAFT';
            const disableOpenButtonBeforeOpen = isBeforeOpen && access === 'HIDE';
            const openButtonText = disableOpenButtonBeforeOpen ? 'Booking opens soon' : 'Open booking';

            const preOpenHideMessage =
              isBeforeOpen && access === 'HIDE' && opensAt
                ? `You will be able to book once booking opens on ${new Date(opensAt).toLocaleString()}.`
                : '';

            const isHideBeforeOpen = isBeforeOpen && access === 'HIDE';

            const preOpenAccessChip =
              isBeforeOpen && access === 'VIEW'
                ? 'Available to preview'
                : isBeforeOpen && access === 'DRAFT'
                ? 'Available to pre-select'
                : '';

            return (
              <article
                className={isHideBeforeOpen ? 'studentProgrammeCard is-pending' : 'studentProgrammeCard'}
                key={id}
              >
                <div className="studentProgrammeCard__head">
                  <div>
                    <h2>{row.name || 'Programme'}</h2>
                    {row.description ? <p>{row.description}</p> : null}
                  </div>
                  {countdownText ? <span className={secToClose !== null ? 'is-open' : ''}>{countdownText}</span> : null}
                </div>
                <div className="studentProgrammeCard__body">
                  {windowText ? <div className="studentProgrammeCard__window">{windowText}</div> : null}
                  {preOpenAccessChip ? <span className="studentProgrammeCard__access">{preOpenAccessChip}</span> : null}
                  {preOpenHideMessage ? <div className="studentProgrammeCard__notice">{preOpenHideMessage}</div> : null}
                  {showSummaryChips ? (
                    <div className="studentProgrammeCard__chips">
                      <span>
                        <strong>{pending}</strong> available
                      </span>
                      <span>
                        <strong>{draft}</strong> draft
                      </span>
                      <span className="is-success">
                        <strong>{accepted}</strong> submitted
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="studentProgrammeCard__footer">
                  <button
                    type="button"
                    className={disableOpenButtonBeforeOpen ? 'parentButton' : 'parentButton parentButton--primary'}
                    disabled={disableOpenButtonBeforeOpen}
                    onClick={() =>
                      history.push(
                        `/clubProgrammes/${id}/booking?playerPermissionId=${encodeURIComponent(playerPermissionId)}`
                      )
                    }
                  >
                    {openButtonText}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
};
